'use client';

import { useEffect, useRef } from 'react';
import { RecentConversations } from './recent-conversations';
import { Transcript, type TranscriptTurn } from './transcript';
import { WaveformBars } from './waveform-bars';

export interface SessionViewProps {
  /** Whether ElevenLabs is currently outputting audio */
  isSpeaking: boolean;
  /** True while ending or session-ended phase (fade-out state) */
  isEnding: boolean;
  onStop: () => void;
  onNewSession: () => void;
  remaining: number | null;
  budget: number;
  turns: TranscriptTurn[];
  online: boolean;
  /**
   * Non-blocking warning string when conversation provisioning failed
   * (signed-in users only). Renders a small indicator in the sidebar so
   * the user knows this session won't appear in history.
   */
  persistError?: string | null;
  /**
   * Current conversation row id, set after `provisionConversation` round-
   * trips. Null for anonymous users (no persistence) and during the
   * brief window before the create call returns. Used by the sidebar
   * "RECENT" list to exclude the active conversation.
   */
  currentConversationId: string | null;
  /**
   * Click handler for a row in the sidebar "RECENT" list. Wires through
   * to voice-panel's `handleResume`, which routes voice resumes via
   * mic-priming and chat resumes straight into the new view.
   */
  onResume: (id: string) => void;
}

export function SessionView({
  isSpeaking,
  isEnding,
  onStop,
  onNewSession,
  remaining,
  budget,
  turns,
  online,
  persistError,
  currentConversationId,
  onResume,
}: SessionViewProps) {
  const chatRef = useRef<HTMLDivElement>(null);

  // Auto-scroll transcript to bottom on new messages
  useEffect(() => {
    chatRef.current?.scrollTo({ top: chatRef.current.scrollHeight, behavior: 'smooth' });
  }, [turns.length]);

  // Once the conversation is underway, the transcript takes over the
  // lower portion of the view and the orb sits compact at the top.
  // Before the first message (and while ending) the orb stays
  // vertically centred in the full area.
  const expandTranscript = turns.length > 0 && !isEnding;

  return (
    <div className="flex mt-[65px] h-[calc(100vh-113px)]">

      {/* ── Left sidebar ──────────────────────────────────────────── */}
      <aside className="hidden md:flex flex-col w-56 lg:w-64 border-r border-outline-variant bg-surface-container-lowest shrink-0">

        {/* Sidebar header */}
        <div className="px-5 pt-6 pb-4 border-b border-outline-variant">
          <div className="flex items-center gap-2.5 mb-1">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src="/logo.png" alt="" className="w-5 h-5 object-contain shrink-0" />
            <span className="font-semibold text-xs text-on-surface tracking-tight">Just Ask Satoshi</span>
          </div>
          <div className="flex items-center gap-1.5 pl-7">
            <div className="w-1 h-1 rounded-full bg-primary-bright" />
            <span className="font-mono text-[9px] tracking-widest text-primary">SESSION LIVE</span>
          </div>
        </div>

        {/* Nav */}
        <div className="flex-1 px-3 pt-4 space-y-1">
          <button
            onClick={onNewSession}
            className="w-full flex items-center gap-2.5 px-3 py-2.5 bg-primary-bright text-on-primary font-mono text-[11px] font-bold tracking-widest hover:bg-primary active:scale-95 transition-all"
          >
            <span className="text-base leading-none">+</span>
            NEW SESSION
          </button>

          <div className="pt-3 pb-1">
            <p className="font-mono text-[9px] tracking-widest text-outline px-3 mb-2">CURRENT</p>
            <div className="flex items-center gap-2.5 px-3 py-2.5 bg-surface-container border border-outline-variant">
              <div className="w-1.5 h-1.5 rounded-full bg-primary-bright shrink-0" />
              <span className="font-mono text-[10px] tracking-widest text-on-surface">This session</span>
            </div>
          </div>

          {persistError && (
            <div className="px-3 mt-3" title={persistError}>
              <p className="font-mono text-[9px] tracking-widest text-amber-400 border border-amber-400/30 px-3 py-2">
                ⚠ HISTORY NOT SAVING
              </p>
            </div>
          )}

          <RecentConversations
            currentConversationId={currentConversationId}
            onResume={onResume}
          />
        </div>

        {/* Sidebar footer — timer */}
        {remaining !== null && (() => {
          const pct = Math.max(0, Math.min(100, (remaining / budget) * 100));
          const isCritical = pct < 15;
          const isWarning  = pct < 35;
          const mins = Math.floor(remaining / 60);
          const secs = String(remaining % 60).padStart(2, '0');
          return (
            <div className={`px-5 py-4 border-t transition-colors duration-500 ${
              isCritical ? 'border-error-text/40 bg-error-text/5' :
              isWarning  ? 'border-primary/30' :
              'border-outline-variant'
            }`}>
              <p className={`font-mono text-[9px] tracking-widest mb-2 transition-colors ${
                isCritical ? 'text-error-text' : 'text-outline'
              }`}>
                {isCritical ? 'ALMOST OUT' : 'TIME REMAINING'}
              </p>
              <div className={`overflow-hidden mb-2 transition-all duration-500 ${
                isCritical ? 'h-[3px]' : isWarning ? 'h-[2px]' : 'h-px'
              } bg-outline-variant`}>
                <div
                  className={`h-full transition-all duration-1000 ${
                    isCritical ? 'bg-error-text animate-pulse' :
                    isWarning  ? 'bg-amber-400' :
                    'bg-primary-bright'
                  }`}
                  style={{ width: `${pct}%` }}
                />
              </div>
              <p className={`font-mono text-[11px] tracking-widest font-bold transition-colors ${
                isCritical ? 'text-error-text animate-pulse' :
                isWarning  ? 'text-amber-400' :
                'text-on-surface-variant'
              }`}>
                {mins}:{secs}
              </p>
            </div>
          );
        })()}
      </aside>

      {/* ── Main voice area ───────────────────────────────────────── */}
      <div className="flex-1 flex flex-col min-w-0 bg-surface-container-low">

        {/* Scanline */}
        <div
          className="absolute inset-0 pointer-events-none z-0"
          style={{
            background:
              'repeating-linear-gradient(0deg, transparent, transparent 3px, rgba(247,147,26,0.008) 3px, rgba(247,147,26,0.008) 4px)',
          }}
        />

        <div className="relative z-10 flex-1 flex flex-col min-h-0">

          {/* Upper zone: orb + live status. Centered & full-height
              until the conversation starts; compact at the top once the
              transcript needs the room. */}
          <div className={`${
            expandTranscript ? 'shrink-0' : 'flex-1'
          } flex flex-col items-center justify-center gap-5 px-6 pt-8 pb-4 min-h-0`}>
            <div
              className={`
                w-44 h-44 md:w-52 md:h-52 rounded-3xl border border-primary/40
                flex items-center justify-center shrink-0
                ${isSpeaking && !isEnding ? 'animate-orb-pulse-fast' : 'animate-orb-pulse'}
                ${isEnding ? 'opacity-40' : ''}
                transition-opacity duration-700
              `}
              style={{
                background: 'radial-gradient(circle at 38% 32%, #f7931a 0%, #a04200 55%, #3d1a00 100%)',
              }}
            >
              <WaveformBars active={isSpeaking && !isEnding} />
            </div>

            {!isEnding ? (
              <div className="flex flex-col items-center gap-3 text-center">
                <div>
                  <p className="text-2xl md:text-3xl font-semibold text-primary tracking-tight mb-1">
                    {isSpeaking ? 'Satoshi is speaking' : 'Listening...'}
                  </p>
                  <p className="font-mono text-[11px] tracking-widest text-on-surface-variant">
                    {isSpeaking ? 'Go ahead and reply when ready' : 'Ask your question'}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 rounded-full bg-primary-bright animate-pulse" />
                  <span className="font-mono text-[11px] tracking-widest text-primary">MICROPHONE ON</span>
                </div>
                {!online && (
                  <p className="font-mono text-[10px] tracking-wider text-primary/70 border border-primary/30 px-3 py-1.5">
                    &#9888; Connection unstable
                  </p>
                )}
              </div>
            ) : (
              <p className="font-mono text-sm tracking-widest text-on-surface-variant">
                ENDING SESSION...
              </p>
            )}
          </div>

          {/* Lower zone: transcript + end button. Grows to fill the
              space below the orb once a conversation is underway. */}
          {!isEnding && (
            <div className={`flex flex-col items-center gap-4 px-6 pb-6 ${
              expandTranscript ? 'flex-1 min-h-0' : ''
            }`}>
              {turns.length > 0 && (
                <div
                  ref={chatRef}
                  className="w-full max-w-xl flex-1 min-h-0 overflow-y-auto border-t border-outline-variant pt-4"
                >
                  <Transcript turns={turns} />
                </div>
              )}
              <button
                onClick={onStop}
                className="flex items-center gap-2 border border-outline-variant text-on-surface-variant font-mono text-[11px] tracking-widest px-8 py-3 hover:border-error-text hover:text-error-text active:scale-95 transition-all"
              >
                &#x2715; END SESSION
              </button>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
